import request from "graphql-request";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";

import { urls } from "@shared/constants";
import { Text } from "@shared/elements";
import { docTitle } from "@shared/utils";

import { TimelineCallPage, TTimelineCallPage } from "__generated__/types";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";
import { deriveTimelineEntries } from "src/utils/deriveTimelineEntries";
import {
  formatTimelineDate,
  isTimelineEntryPast,
} from "src/utils/formatTimelineDate";

type Props = {
  data: TTimelineCallPage;
};

export default function Timeline({ data }: Props) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Timeline`);
  return (
    <>
      <Head>
        <title key="title">
          {docTitle(t("documentTitle"), data.callPage?.call.title.en)}
        </title>
        <meta
          property="og:title"
          content={docTitle(t("documentTitle"), data.callPage?.call.title.en)}
          key="og-title"
        />
        <meta
          property="og:url"
          content={`${urls.callPage(data.callPage?.slug ?? "")}/timeline`}
          key="og-url"
        />
      </Head>
      <div className="flex flex-col items-center mx-auto pt-14 pb-8 lg:py-14">
        <div className="md:w-[700px] w-full px-4 md:px-0 mx-auto divide-y-[1px] divide-gray-200 border-b-[1px] border-gray-200">
          <Text as="h2" size="3xl" weight="bold">
            {t("title")}
          </Text>
          <dl className="w-full mt-8 divide-y divide-gray-200">
            {data.callPage?.call.timeline.map((entry, index) => (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 py-5"
                key={`${entry.title}${index}`}
              >
                <Text color={isTimelineEntryPast(entry) ? "muted" : "default"}>
                  {formatTimelineDate(entry)}
                </Text>
                <Text weight="semibold">{entry.title}</Text>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.Timeline`,
  ]);

  const slug: string = params?.slug as string;

  const data: TTimelineCallPage | null = await request(
    `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
    TimelineCallPage,
    { slug },
  );

  if (!messages.apps) return { notFound: true, revalidate: 60 };

  const timelinePageMessages = messages.apps["call-pages"].pages.Timeline;

  if (!data?.callPage) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  data.callPage.call.timeline = deriveTimelineEntries({
    submissionStartDate: data.callPage.call.submissionStartDate,
    submissionEndDate: data.callPage.call.submissionEndDate,
    resultsAnnouncementDate: data.callPage.call.resultsAnnouncementDate,
    resultsAnnouncementShowDay: data.callPage.call.resultsAnnouncementShowDay,
    customEntries: data.callPage.call.timeline ?? [],
    labels: {
      submissionStart: timelinePageMessages.submissionStart,
      submissionEnd: timelinePageMessages.submissionEnd,
      resultsAnnouncement: timelinePageMessages.resultsAnnouncement,
    },
  });

  return {
    props: {
      data,
      messages,
    },
    revalidate: 60,
  };
}
